import { useState, useEffect, useCallback, useRef, useMemo, ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useInteractionReady } from "@/components/ui/InteractionGate";
import { PLACEHOLDER_URL, renderUrl } from "@/lib/imageUtils";
import { useThemeLayout } from "@/hooks/useThemeLayout";
import CarmaxPageHero from "@/components/carmax/CarmaxPageHero";

export interface HeroSlide {
  image: string;
  /** Optional 1366-wide desktop variant for srcset on mid-size screens. */
  image1366?: string | undefined;
  imageMobile?: string | undefined;
  title?: string | undefined;
  subtitle?: string | undefined;
  cta?: ReactNode | undefined;
  fit?: "cover" | "contain" | undefined;
  hideText?: boolean | undefined;
}

interface HeroSliderProps {
  slides: HeroSlide[];
  cmsSlug?: string;
  autoPlay?: boolean;
  interval?: number;
  overlay?: boolean;
  height?: string;
  children?: ReactNode;
  /**
   * Emit a real mobile image as the <img> fallback (< 768px).
   * Default true. The homepage passes false because it renders its own
   * dedicated mobile hero (MobileHeroStrip) and the slider is desktop-only —
   * a mobile src there would download bytes that never paint.
   */
  mobileImages?: boolean;
}

const HERO_SIZES = "(max-width: 768px) 100vw, 100vw";
const EMPTY_IMAGE = "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";

/**
 * Rewrite legacy myrvpart.com/images/splash-assets/... URLs (still present
 * in some CMS rows) to their Supabase splash-assets equivalents so the
 * rendered <source srcset> byte-matches the SSR <link rel="preload"> emitted
 * by src/routes/index.tsx (via src/lib/heroPreload.ts). Without this the
 * desktop LCP preload is wasted and the image is discovered only after
 * React hydrates.
 */
const normalizeHeroUrl = (url?: string): string | undefined => {
  if (!url) return url;
  return url.replace(
    /^https?:\/\/(?:www\.)?myrvpart\.com\/images\/splash-assets\//,
    "/images/splash-assets/"
  );
};

const imgRetry = (e: React.SyntheticEvent<HTMLImageElement>) => {
  const img = e.currentTarget;
  const retryCount = parseInt(img.dataset['retried'] || "0", 10);

  if (retryCount === 0 && img.src.includes("/render/image/")) {
    // Render API failed → try raw /object/ URL
    img.dataset['retried'] = "1";
    img.src = img.src.replace("/render/image/", "/object/").split("?")[0] || img.src;
  } else if (retryCount < 2) {
    // Cache-bust retry
    img.dataset['retried'] = String(retryCount + 1);
    const base = img.src.split("?")[0];
    img.src = base + "?retry=" + Date.now();
  } else {
    // Final fallback
    img.src = PLACEHOLDER_URL;
  }
};

const HeroSlider = ({
  slides,
  cmsSlug,
  autoPlay = true,
  interval = 6000,
  overlay = true,
  height = "min-h-[400px]",
  children,
  mobileImages = true,
}: HeroSliderProps) => {
  const themeLayout = useThemeLayout();
  const isMobile = useIsMobile();
  // Static-first hydration: the initial commit renders ONLY slide 0 and no
  // controls, so hydration touches a fraction of the nodes. The rotation
  // slides, arrows, and dots attach on first interaction (or after a short
  // idle window for sessions that never interact).
  const controlsReady = useInteractionReady();
  const [current, setCurrent] = useState(0);
  const hasNavigatedRef = useRef(false);
  // Slides whose <source srcset> may be emitted. Only slide 0 starts warmed
  // so hidden slides don't trigger eager image downloads (Lighthouse was
  // flagging a 215 KiB hidden-slide fetch). Everything warms on the first
  // user interaction (when autoplay starts) — well ahead of the 6s rotation.
  const [warmed, setWarmed] = useState<ReadonlySet<number>>(() => new Set([0]));
  const warmAll = useCallback(() => {
    setWarmed((prev) =>
      prev.size === slides.length ? prev : new Set(slides.map((_, i) => i)),
    );
  }, [slides]);
  const warmSlide = useCallback((i: number) => {
    setWarmed((prev) => {
      if (prev.has(i)) return prev;
      const next = new Set(prev);
      next.add(i);
      return next;
    });
  }, []);
  // Start the first slide visible. The hero image is preloaded with
  // fetchpriority=high in index.html, so by the time React mounts the
  // bytes are already in the memory cache and the <img> paints in the
  // same frame. Hiding it behind opacity-0 until onLoad was adding
  // ~250ms of paint delay on top of hydration time.
  const [firstImageLoaded, setFirstImageLoaded] = useState(true);
  const [mounted, setMounted] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const firstImgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const markHeroReady = useCallback(() => {
    setFirstImageLoaded(true);
    try {
      window.dispatchEvent(new Event('hero-ready'));
    } catch {}
  }, []);

  // Cached-image safeguard: hide static shell as soon as React img is in DOM.
  // The bytes are already preloaded, so the React <img> hits the memory cache
  // and is `complete` synchronously. Also schedule an rAF fallback.
  useEffect(() => {
    const img = firstImgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      markHeroReady();
      return;
    }
    const raf = requestAnimationFrame(() => {
      const i = firstImgRef.current;
      if (i && i.complete && i.naturalWidth > 0) markHeroReady();
    });
    return () => cancelAnimationFrame(raf);
  }, [markHeroReady]);

  // Crossfade: every slide's <img> stays mounted (stacked, opacity-toggled),
  // but its <source srcset> is only emitted once the slide is "warmed" (see
  // above), so hidden slides cost zero network until the user engages.
  const next = useCallback(() => {
    hasNavigatedRef.current = true;
    setCurrent((prev) => (prev + 1) % slides.length);
    setWarmed((prev) => (prev.size === slides.length ? prev : new Set(slides.map((_, i) => i))));
  }, [slides]);

  const prev = useCallback(() => {
    hasNavigatedRef.current = true;
    setCurrent((p) => (p - 1 + slides.length) % slides.length);
    setWarmed((prev) => (prev.size === slides.length ? prev : new Set(slides.map((_, i) => i))));
  }, [slides]);

  const goTo = useCallback((i: number) => {
    if (i === current) return;
    hasNavigatedRef.current = true;
    setCurrent(i);
    warmSlide(i);
  }, [current, warmSlide]);

  // Static hero shell is hidden only after the React first-slide image
  // has actually decoded (see onLoad below). This prevents a flash of
  // background color on mobile where the React <img> may still be loading.

  useEffect(() => {
    if (!controlsReady || window.matchMedia("(prefers-reduced-motion: reduce)").matches || !autoPlay || slides.length <= 1 || isMobile) return;
    warmAll();
    const intervalId = setInterval(next, interval);
    timeoutRef.current = intervalId;
    return () => clearInterval(intervalId);
  }, [controlsReady, autoPlay, interval, next, slides.length, isMobile, warmAll]);


  if (themeLayout === "carmax") {
    return (
      <CarmaxPageHero cmsSlug={cmsSlug} slides={slides} autoPlay={autoPlay} interval={interval}>
        {children}
      </CarmaxPageHero>
    );
  }

  if (slides.length === 0) {
    return (
      <section className="relative flex aspect-[9/14] sm:aspect-[4/3] md:aspect-[3/2] lg:aspect-[21/9] md:min-h-[560px] lg:min-h-[400px] items-center justify-center overflow-hidden bg-secondary -mt-[78px] pt-[78px] sm:-mt-[140px] sm:pt-[140px]" />
    );
  }

  const slide = slides[current];
  if (!slide) return null;
  const isFirst = current === 0;
  // Slides rendered in this commit. Pre-interaction that is slide 0 only.
  const renderedSlides = controlsReady ? slides : slides.slice(0, 1);

  return (
    <section className={`relative flex aspect-[9/14] sm:aspect-[4/3] md:aspect-[3/2] lg:aspect-[21/9] md:min-h-[560px] lg:min-h-[400px] items-center justify-center overflow-hidden bg-secondary -mt-[78px] pt-[78px] sm:-mt-[140px] sm:pt-[140px]`}>
      {/* All slides stay mounted and stacked; the active one crossfades in.
          This guarantees the incoming image is already decoded before it
          becomes visible — no blank placeholder between rotations. */}
      {renderedSlides.map((s, i) => {
        // Normalize URLs so the rendered <source srcset> byte-matches the SSR
        // preload hrefs from src/routes/index.tsx (Supabase splash-assets origin).
        const heroImage = normalizeHeroUrl(s.image);
        const heroImage1366 = normalizeHeroUrl(s.image1366);
        const heroImageMobile = normalizeHeroUrl(s.imageMobile);
        // Route CMS-hosted (Supabase storage) desktop images through the
        // Render API at quality 50 so oversized raw uploads aren't shipped.
        // renderUrl passes same-origin /images/splash-assets/ paths through
        // unchanged, keeping byte-parity with the SSR hero preload.
        const heroSrc1920 = heroImage ? renderUrl(heroImage, 1920, 50) : undefined;
        const heroSrc1366 = heroImage ? renderUrl(heroImage1366 ?? heroImage, 1366, 50) : undefined;
        // Mobile (< 768px): the desktop <source> is media-gated, so without
        // this the <img> falls back to EMPTY_IMAGE and the hero renders blank.
        const mobileSource = heroImageMobile ?? heroImage;
        const heroSrcMobile =
          mobileImages && mobileSource ? renderUrl(mobileSource, 750, 55) : undefined;
        const active = i === current;
        const visible = i === 0
          ? (active ? firstImageLoaded : false)
          : active;
        // Only emit <source> for warmed slides (or the active one, which must
        // never be blocked) so hidden slides don't download at page load.
        const emitSource = warmed.has(i) || active;
        return (
          <picture key={i} className="pointer-events-none" aria-hidden={!active}>
            {heroSrc1920 && emitSource && (
              <source
                srcSet={`${heroSrc1366} 1366w, ${heroSrc1920} 1920w`}
                sizes="100vw"
                media="(min-width: 768px)"
                type="image/webp"
              />
            )}
            <img
              ref={i === 0 ? firstImgRef : undefined}
              src={heroSrcMobile && emitSource ? heroSrcMobile : EMPTY_IMAGE}
              alt={active ? (s.title || "") : ""}
              width={1920}
              height={1080}
              className={`absolute inset-0 h-full w-full ${s.fit === "contain" ? "object-contain" : "object-cover"} transition-opacity duration-500 ${
                visible ? "opacity-100" : "opacity-0"
              }`}
              style={{ transform: 'translate3d(0,0,0)' }}
              fetchPriority={i === 0 ? "high" : "low"}
              decoding={i === 0 ? "sync" : "async"}
              loading={i === 0 ? "eager" : "lazy"}
              onLoad={i === 0 ? markHeroReady : undefined}
              onError={imgRetry}
            />
          </picture>
        );
      })}

      {overlay && !slide.hideText && (
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/80 via-secondary/50 to-secondary/80" />
      )}

      {/* SEO/a11y fallback: when hero text is hidden via CMS (hideText),
          the page would otherwise have no H1 at all. */}
      {slide.hideText && (
        <h1 className="sr-only">Rex &amp; Sons RVs — New &amp; Used RV Dealer in Castle Hayne, NC</h1>
      )}

      {!slide.hideText && (
      <div className="relative z-10 container py-16 sm:py-12 lg:py-16 sm:px-20 lg:px-8 text-center">
        <div
          key={`content-${current}`}
          className={`${mounted && !isMobile && hasNavigatedRef.current ? "hero-slide-enter" : ""} min-h-[360px] sm:min-h-[260px] md:min-h-[280px] lg:min-h-[280px] flex flex-col justify-center`}
        >
          <div>
            {slide.title ? (
              <h1 className="font-heading text-3xl font-bold uppercase tracking-wide text-primary-foreground md:text-5xl lg:text-6xl break-words">
                {slide.title}
              </h1>
            ) : (
              /* SEO/a11y fallback: a page whose hero slide has no CMS title
                 still needs an H1. Visually hidden so the design is unchanged. */
              <h1 className="sr-only">Rex &amp; Sons RVs — New &amp; Used RV Dealer in Castle Hayne, NC</h1>
            )}
            {slide.subtitle && (
              <p className="mx-auto mt-4 max-w-2xl text-lg text-secondary-foreground/70">
                {slide.subtitle}
              </p>
            )}
          </div>
          <div className="mt-6 min-h-[44px] flex items-center justify-center">
            {slide.cta ?? children}
          </div>
        </div>
      </div>
      )}

      {controlsReady && slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-2 sm:left-4 top-[35%] sm:top-[38%] lg:top-1/2 z-30 -translate-y-1/2 rounded-full bg-secondary/60 p-2 text-primary-foreground backdrop-blur-sm transition hover:bg-secondary/80"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={next}
            className="absolute right-2 sm:right-20 lg:right-4 top-[35%] sm:top-[38%] lg:top-1/2 z-30 -translate-y-1/2 rounded-full bg-secondary/60 p-2 text-primary-foreground backdrop-blur-sm transition hover:bg-secondary/80"
            aria-label="Next slide"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {!isMobile && (
            <div className="absolute bottom-2 lg:bottom-4 left-1/2 z-20 hidden sm:hidden lg:flex -translate-x-1/2 gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className="flex h-11 w-11 items-center justify-center"
                  aria-label={`Go to slide ${i + 1}`}
                >
                  <span className={`block rounded-full transition-all ${
                    i === current ? "h-3 w-8 bg-primary" : "h-3 w-3 bg-primary-foreground/40"
                  }`} />
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default HeroSlider;
